import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    country: '',
    inquiryType: 'general',
    message: ''
  });

  const [quoteData, setQuoteData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    country: '',
    quantity: '',
    grade: 'standard',
    deliveryLocation: '',
    timeframe: '',
    requirements: ''
  });

  const [isSubmittingQuote, setIsSubmittingQuote] = useState(false);
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingContact(true);
    
    try {
      await axios.post(`${API}/inquiries`, formData);
      
      toast.success("Message Sent!", {
        description: "We'll get back to you within 24 hours.",
      });
      
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        country: '',
        inquiryType: 'general',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error("Submission Failed", {
        description: error.response?.data?.detail || "Please try again later.",
      });
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingQuote(true);
    
    try {
      await axios.post(`${API}/quotes`, quoteData);
      
      toast.success("Quote Request Received!", {
        description: "Our team will prepare a detailed quote and contact you soon.",
      });
      
      setQuoteData({
        name: '',
        email: '',
        company: '',
        phone: '',
        country: '',
        quantity: '',
        grade: 'standard',
        deliveryLocation: '',
        timeframe: '',
        requirements: ''
      });
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error("Submission Failed", {
        description: error.response?.data?.detail || "Please try again later.",
      });
    } finally {
      setIsSubmittingQuote(false);
    }
  };

  const handleContactChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleQuoteChange = (e) => {
    setQuoteData({ ...quoteData, [e.target.name]: e.target.value });
  };

  return (
    <div className="page-container">
      {/* Hero Section */}
      <section className="page-hero">
        <h1 className="display-huge">Contact Us</h1>
        <p className="body-large hero-description">
          Get in touch with our team for inquiries, quotes, or partnership opportunities
        </p>
      </section>

      {/* Contact Info */}
      <section className="contact-info-section">
        <div className="contact-info-grid">
          <div className="contact-info-card">
            <Mail size={32} />
            <h3 className="heading-2">Email</h3>
            <p className="body-medium">sales@spacegems.io</p>
            <p className="body-small">We reply within 24 hours</p>
          </div>
          <div className="contact-info-card">
            <Phone size={32} />
            <h3 className="heading-2">Phone</h3>
            <p className="body-medium">+1 (347) 699-0915</p>
            <p className="body-small">Mon-Fri, 9AM-6PM EST</p>
          </div>
          <div className="contact-info-card">
            <MapPin size={32} />
            <h3 className="heading-2">Offices</h3>
            <p className="body-medium">Ratoma, Conakry, Guinea</p>
            <p className="body-medium">399 East 8th Street, New York, NY USA</p>
          </div>
        </div>
      </section>

      {/* Quote Request Form */}
      <section className="form-section">
        <div className="form-container">
          <div className="form-header">
            <h2 className="display-medium">Request a Quote</h2>
            <p className="body-medium">Fill out the form below to receive a detailed quote for your bauxite requirements</p>
          </div>
          <form onSubmit={handleQuoteSubmit} className="contact-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="quote-name" className="form-label">Full Name *</label>
                <input
                  type="text"
                  id="quote-name"
                  name="name"
                  value={quoteData.name}
                  onChange={handleQuoteChange}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="quote-email" className="form-label">Email Address *</label>
                <input
                  type="email"
                  id="quote-email"
                  name="email"
                  value={quoteData.email}
                  onChange={handleQuoteChange}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="quote-company" className="form-label">Company Name *</label>
                <input
                  type="text"
                  id="quote-company"
                  name="company"
                  value={quoteData.company}
                  onChange={handleQuoteChange}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="quote-phone" className="form-label">Phone Number *</label>
                <input
                  type="tel"
                  id="quote-phone"
                  name="phone"
                  value={quoteData.phone}
                  onChange={handleQuoteChange}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="quote-country" className="form-label">Country *</label>
                <input
                  type="text"
                  id="quote-country"
                  name="country"
                  value={quoteData.country}
                  onChange={handleQuoteChange}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="quote-quantity" className="form-label">Required Quantity (tons) *</label>
                <input
                  type="number"
                  id="quote-quantity"
                  name="quantity"
                  value={quoteData.quantity}
                  onChange={handleQuoteChange}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="quote-grade" className="form-label">Bauxite Grade *</label>
                <select
                  id="quote-grade"
                  name="grade"
                  value={quoteData.grade}
                  onChange={handleQuoteChange}
                  className="form-select"
                  required
                >
                  <option value="standard">Standard Grade</option>
                  <option value="premium">Premium Grade</option>
                  <option value="high-alumina">High Alumina</option>
                  <option value="custom">Custom Specifications</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="quote-location" className="form-label">Delivery Location *</label>
                <input
                  type="text"
                  id="quote-location"
                  name="deliveryLocation"
                  value={quoteData.deliveryLocation}
                  onChange={handleQuoteChange}
                  className="form-input"
                  placeholder="Port or facility"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="quote-timeframe" className="form-label">Delivery Timeframe *</label>
              <input
                type="text"
                id="quote-timeframe"
                name="timeframe"
                value={quoteData.timeframe}
                onChange={handleQuoteChange}
                className="form-input"
                placeholder="e.g., Q3 2025, Within 60 days"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="quote-requirements" className="form-label">Additional Requirements</label>
              <textarea
                id="quote-requirements"
                name="requirements"
                value={quoteData.requirements}
                onChange={handleQuoteChange}
                className="form-textarea"
                rows="4"
                placeholder="Specific quality requirements, delivery terms, etc."
              />
            </div>

            <button type="submit" className="btn-primary form-submit" disabled={isSubmittingQuote}>
              <Send size={20} />
              {isSubmittingQuote ? 'Submitting...' : 'Submit Quote Request'}
            </button>
          </form>
        </div>
      </section>

      {/* General Contact Form */}
      <section className="form-section" style={{ background: 'var(--bg-secondary)' }}>
        <div className="form-container">
          <div className="form-header">
            <h2 className="display-medium">General Inquiry</h2>
            <p className="body-medium">Have a question or want to learn more about our services?</p>
          </div>
          <form onSubmit={handleContactSubmit} className="contact-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="contact-name" className="form-label">Full Name *</label>
                <input
                  type="text"
                  id="contact-name"
                  name="name"
                  value={formData.name}
                  onChange={handleContactChange}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-email" className="form-label">Email Address *</label>
                <input
                  type="email"
                  id="contact-email"
                  name="email"
                  value={formData.email}
                  onChange={handleContactChange}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="contact-company" className="form-label">Company Name</label>
                <input
                  type="text"
                  id="contact-company"
                  name="company"
                  value={formData.company}
                  onChange={handleContactChange}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-phone" className="form-label">Phone Number</label>
                <input
                  type="tel"
                  id="contact-phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleContactChange}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="contact-country" className="form-label">Country</label>
                <input
                  type="text"
                  id="contact-country"
                  name="country"
                  value={formData.country}
                  onChange={handleContactChange}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-type" className="form-label">Inquiry Type *</label>
                <select
                  id="contact-type"
                  name="inquiryType"
                  value={formData.inquiryType}
                  onChange={handleContactChange}
                  className="form-select"
                  required
                >
                  <option value="general">General Inquiry</option>
                  <option value="partnership">Partnership Opportunity</option>
                  <option value="technical">Technical Question</option>
                  <option value="support">Customer Support</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="contact-message" className="form-label">Message *</label>
              <textarea
                id="contact-message"
                name="message"
                value={formData.message}
                onChange={handleContactChange}
                className="form-textarea"
                rows="5"
                required
              />
            </div>

            <button type="submit" className="btn-primary form-submit" disabled={isSubmittingContact}>
              <Send size={20} />
              {isSubmittingContact ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;